import { useCallback, useEffect, useState } from "react";
import axios from "../../api/axios";
import SidebarClient from "../../components/SidebarClient";
import QRCode from "react-qr-code";

export default function WhatsAppConnect() {
  const [status, setStatus] = useState("DISCONNECTED");
  const [qrCode, setQrCode] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initStartedAt, setInitStartedAt] = useState(null);
  const [apiKey, setApiKey] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false); // Toggle to show/hide API key

  // 2FA modal for API key regeneration
  const [showApiKey2fa, setShowApiKey2fa] = useState(false);
  const [apiKeyTotp, setApiKeyTotp] = useState("");
  const [apiKeyTotpError, setApiKeyTotpError] = useState("");
  const [apiKeyTotpBusy, setApiKeyTotpBusy] = useState(false);

  // Send text (single)
  const [message, setMessage] = useState("");

  // Send media (single)
  const [to, setTo] = useState("");
  const [caption, setCaption] = useState("");
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [planInfo, setPlanInfo] = useState({ planName: "", daysRemaining: null });

  const checkStatus = useCallback(async () => {
    try {
      const res = await axios.get("/whatsapp/status");
      const nextStatus = res.data.status;
      setPhoneNumber(res.data.phoneNumber);

      // Prevent flicker back to DISCONNECTED right after init
      if (
        initStartedAt &&
        (status === "INITIALIZING" || status === "WAITING") &&
        nextStatus === "DISCONNECTED" &&
        Date.now() - initStartedAt < 30000
      ) {
        return;
      }

      setStatus(nextStatus);
    } catch (err) {
      console.error("Status check error:", err);
    }
  }, [initStartedAt, status]);

  const fetchApiKey = useCallback(async () => {
    try {
      const res = await axios.get("/whatsapp/api-key");
      setApiKey(res.data.apiKey);
    } catch (err) {
      console.error("API key fetch error:", err);
    }
  }, []);

  // Check status on load
  useEffect(() => {
    checkStatus();
    fetchApiKey();
    axios.get("/client/dashboard-stats").then((res) => {
      setPlanInfo({
        planName: res.data?.planName || "No Plan",
        daysRemaining: res.data?.daysRemaining ?? null,
      });
    }).catch(() => {});
  }, [checkStatus, fetchApiKey]);

  // Keep status updated without refresh
  useEffect(() => {
    const interval = setInterval(() => {
      checkStatus();
    }, 3000);
    return () => clearInterval(interval);
  }, [checkStatus]);

  // Poll for QR code continuously (don't stop until CONNECTED)
  useEffect(() => {
    let interval;

    // Always poll for QR if not connected
    if (status !== "CONNECTED") {
      interval = setInterval(async () => {
        try {
          const res = await axios.get("/whatsapp/qr");
          if (res.data.status === "CONNECTED") {
            setStatus("CONNECTED");
            setQrCode(null);
            setInitStartedAt(null);
            checkStatus(); // Get phone number
          } else if (res.data.status === "QR_READY") {
            setQrCode(res.data.qr); // Update QR even if already showing
            setStatus("QR_READY");
            setInitStartedAt(null);
          } else {
            // INITIALIZING, WAITING, DISCONNECTED, etc.
            setQrCode(null);
            if (
              initStartedAt &&
              (status === "INITIALIZING" || status === "WAITING") &&
              res.data.status === "DISCONNECTED" &&
              Date.now() - initStartedAt < 30000
            ) {
              setStatus("INITIALIZING");
            } else {
              setStatus(res.data.status);
            }
          }
        } catch (err) {
          console.error("QR poll error:", err);
        }
      }, 2000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [status, initStartedAt, checkStatus]);

  const handleConnect = async () => {
    setLoading(true);
    try {
      await axios.post("/whatsapp/init");
      setInitStartedAt(Date.now());
      setStatus("INITIALIZING");
    } catch (err) {
      alert(err?.response?.data?.message || err?.response?.data?.error || "Failed to initialize WhatsApp");
    }
    setLoading(false);
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Are you sure you want to disconnect WhatsApp?")) return;

    setLoading(true);
    try {
      await axios.post("/whatsapp/disconnect");
      setStatus("DISCONNECTED");
      setQrCode(null);
      setPhoneNumber(null);
    } catch (err) {
      alert("Failed to disconnect");
    }
    setLoading(false);
  };

  const handleGenerateApiKey = async () => {
    if (apiKey && !window.confirm("This will replace your existing API key. Continue?")) return;

    try {
      const res = await axios.post("/whatsapp/generate-api-key", {});
      setApiKey(res.data.apiKey);
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;

      // If 2FA is enabled, backend requires a TOTP code for regenerating the API key.
      if (status === 401 && data?.requires2fa) {
        setApiKeyTotp("");
        setApiKeyTotpError("");
        setShowApiKey2fa(true);
        return;
      }

      console.error("Generate API key error:", err);
      alert(data?.error || data?.message || "Failed to generate API key");
    }
  };

  const submitApiKeyTotp = async () => {
    if (!apiKeyTotp) {
      setApiKeyTotpError("Google Authenticator code is required");
      return;
    }

    setApiKeyTotpBusy(true);
    setApiKeyTotpError("");

    try {
      const res = await axios.post("/whatsapp/generate-api-key", { totp: apiKeyTotp });
      setApiKey(res.data.apiKey);
      setShowApiKey2fa(false);
      setApiKeyTotp("");
    } catch (err) {
      setApiKeyTotpError(err?.response?.data?.message || "Invalid 2FA code");
    }

    setApiKeyTotpBusy(false);
  };

  const copyApiKey = () => {
    navigator.clipboard.writeText(apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSendUnified = async () => {
    if (!to) {
      alert("Number is required");
      return;
    }

    // If a file is selected, send media (caption optional)
    if (file) {
      setSending(true);
      try {
        const form = new FormData();
        form.append("to", to);
        form.append("message", caption || message || "");
        form.append("file", file);

        await axios.post("/whatsapp/send-media", form);

        alert("Media sent");
        setTo("");
        setCaption("");
        setMessage("");
        setFile(null);
      } catch (err) {
        alert(err?.response?.data?.error || err?.response?.data?.message || err?.message || "Media send failed");
      }
      setSending(false);
      return;
    }

    // No file: send text message
    if (!message) {
      alert("Message is required");
      return;
    }

    setSending(true);
    try {
      await axios.post("/whatsapp/send", { to, message });
      alert("Message sent");
      setTo("");
      setMessage("");
      setCaption("");
      setFile(null);
    } catch (err) {
      // Prefer backend "error" detail if present (backend sends { message: 'Send failed', error: '...' }).
      alert(err?.response?.data?.error || err?.response?.data?.message || err?.message || "Send failed");
    }
    setSending(false);
  };

  return (
    <div className="flex flex-col lg:flex-row h-screen bg-gray-50">
      <SidebarClient />
      <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-y-auto mt-14 lg:mt-0">
        <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-6 md:mb-8">WhatsApp Connect</h1>

        {(planInfo.planName === "No Plan" || (planInfo.daysRemaining !== null && planInfo.daysRemaining <= 0)) && (
          <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-sm">
            <div className="flex items-center gap-3">
              <span className="text-2xl">⚠️</span>
              <div>
                <p className="text-red-800 font-bold text-sm">
                  {planInfo.planName === "No Plan" ? "No Active Subscription Plan" : "Your Subscription Plan Has Expired"}
                </p>
                <p className="text-red-600 text-xs">
                  Active subscription plan required to connect WhatsApp or send messages.
                </p>
              </div>
            </div>
            <a
              href="/client/plan-renewal"
              className="bg-red-600 hover:bg-red-700 text-white text-xs font-bold px-4 py-2 rounded-xl transition whitespace-nowrap"
            >
              Renew / Choose Plan →
            </a>
          </div>
        )}

        {showApiKey2fa && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50 p-4">
            <div className="bg-white p-8 rounded-3xl w-full max-w-md shadow-2xl">
              <h3 className="text-2xl font-bold mb-2 text-gray-800">2FA Required</h3>
              <p className="text-gray-500 mb-6">
                Enter the 6-digit code from Google Authenticator to regenerate your API key.
              </p>

              <div className="space-y-4">
                <input
                  className="w-full border p-3 rounded-xl"
                  placeholder="6-digit code"
                  value={apiKeyTotp}
                  onChange={(e) => setApiKeyTotp(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") submitApiKeyTotp();
                  }}
                />

                {apiKeyTotpError && (
                  <p className="text-sm text-red-600 font-bold">{apiKeyTotpError}</p>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowApiKey2fa(false);
                      setApiKeyTotp("");
                      setApiKeyTotpError("");
                    }}
                    disabled={apiKeyTotpBusy}
                    className="flex-1 bg-gray-100 py-3 rounded-xl font-bold text-gray-700 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={submitApiKeyTotp}
                    disabled={apiKeyTotpBusy}
                    className="flex-1 bg-blue-600 text-white py-3 rounded-xl font-bold disabled:opacity-50"
                  >
                    {apiKeyTotpBusy ? "Verifying..." : "Verify"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Connection Card */}
          <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
            <h2 className="text-xl font-bold mb-6 text-gray-800">Connection Status</h2>

            {/* Status Badge */}
            <div className="flex items-center gap-3 mb-6">
              <div className={`w-4 h-4 rounded-full ${status === "CONNECTED" ? "bg-green-500 animate-pulse" :
                  status === "QR_READY" || status === "INITIALIZING" ? "bg-yellow-500 animate-pulse" :
                    "bg-red-500"
                }`} />
              <span className="font-bold text-gray-700">
                {status === "CONNECTED" ? "Connected" :
                  status === "QR_READY" ? "Scan QR Code" :
                    status === "INITIALIZING" || status === "WAITING" ? "Initializing..." :
                      "Disconnected"}
              </span>
              {status === "CONNECTED" && phoneNumber && (
                <span className="text-green-600 font-mono">+{phoneNumber}</span>
              )}
            </div>

            {status === "QR_READY" && (
              <div className="mb-6 rounded-xl border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-900">
                <div className="font-extrabold">QR Required (WhatsApp logged out)</div>
                <div className="mt-1">
                  If QR appears again after server restart, WhatsApp ended the old session.
                  Scan QR from your phone to reconnect. This is required by WhatsApp.
                </div>
              </div>
            )}

            {status === "DISCONNECTED" && (
              <div className="mb-6 rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
                <div className="font-bold">Not connected</div>
                <div className="mt-1">
                  Click <b>Connect WhatsApp</b>. If QR shows, scan it once and it will restore automatically on future restarts.
                </div>
              </div>
            )}

            {/* QR Code Display */}
            {(status === "QR_READY" && qrCode) && (
              <div className="bg-white p-6 rounded-xl border-2 border-dashed border-gray-200 flex justify-center mb-6">
                {typeof qrCode === "string" && qrCode.startsWith("data:image") ? (
                  <img src={qrCode} alt="WhatsApp QR" style={{ width: 220, height: 220 }} />
                ) : (
                  <QRCode value={qrCode} size={220} />
                )}
              </div>
            )}

            {/* Waiting for QR */}
            {(status === "INITIALIZING" || status === "WAITING") && !qrCode && (
              <div className="bg-gray-50 p-12 rounded-xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center mb-6">
                <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4" />
                <p className="text-gray-500">Generating QR Code...</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-4">
              {status === "DISCONNECTED" && (
                <button
                  onClick={handleConnect}
                  disabled={loading}
                  className="flex-1 bg-green-600 text-white py-3 px-6 rounded-xl font-bold hover:bg-green-700 transition disabled:opacity-50"
                >
                  {loading ? "Connecting..." : "Connect WhatsApp"}
                </button>
              )}

              {status === "CONNECTED" && (
                <button
                  onClick={handleDisconnect}
                  disabled={loading}
                  className="flex-1 bg-red-600 text-white py-3 px-6 rounded-xl font-bold hover:bg-red-700 transition disabled:opacity-50"
                >
                  {loading ? "Disconnecting..." : "Disconnect"}
                </button>
              )}
            </div>

            {status === "QR_READY" && (
              <p className="text-sm text-gray-500 mt-4 text-center">
                Open WhatsApp on your phone &rarr; Settings &rarr; Linked Devices &rarr; Link a Device
              </p>
            )}
          </div>

          {/* API Key Card */}
          <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
            <h2 className="text-xl font-bold mb-2 text-gray-800">API Key</h2>
            <p className="text-gray-500 text-sm mb-6">Use this key to send messages via API</p>

            {apiKey ? (
              <div className="space-y-4">
                <div className="bg-gray-900 text-green-400 p-4 rounded-xl font-mono text-sm break-all">
                  {showApiKey ? apiKey : apiKey.substring(0, 8) + "••••••••••••••••••••••••••••••••"}
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-bold hover:bg-gray-200 transition"
                  >
                    {showApiKey ? "🙈 Hide" : "👁️ Show"}
                  </button>
                  <button
                    onClick={copyApiKey}
                    className="flex-1 bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 transition"
                  >
                    {copied ? "✅ Copied!" : "Copy Key"}
                  </button>
                  <button
                    onClick={handleGenerateApiKey}
                    className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-bold hover:bg-gray-200 transition"
                  >
                    Regenerate
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={handleGenerateApiKey}
                className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 transition"
              >
                Generate API Key
              </button>
            )}

            {/* Send Message (single) - Text or Media */}
            <div className="mt-10 border-t pt-8">
              <h3 className="font-bold text-gray-800 mb-3">Send Message (Text / Photo / PDF)</h3>
              <div className="space-y-3">
                <input
                  className="w-full border p-3 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                  placeholder="Enter number (e.g. 9198xxxxxxx)"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                />
                <textarea
                  className="w-full border p-3 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                  placeholder="Message (required if no file)"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
                <textarea
                  className="w-full border p-3 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                  placeholder="Caption (optional, used when file is selected)"
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                />
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
                <button
                  onClick={handleSendUnified}
                  disabled={sending || status !== "CONNECTED"}
                  className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 transition disabled:opacity-50"
                >
                  {sending ? "Sending..." : "Send"}
                </button>
                {status !== "CONNECTED" && (
                  <p className="text-xs text-gray-500">Connect WhatsApp first to send.</p>
                )}
                {file && !caption && message && (
                  <p className="text-xs text-gray-500">Message will be used as caption for the file.</p>
                )}
              </div>
            </div>

            {/* API Usage Example */}
            <div className="mt-8">
              <h3 className="font-bold text-gray-700 mb-3">API Usage</h3>
              <div className="bg-gray-900 text-gray-300 p-4 rounded-xl text-xs overflow-x-auto">
                <pre>{`curl -X POST ${window.location.origin}/api/whatsapp/api/send \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_API_KEY" \\
  -d '{"to": "919876543210", "message": "Hello!"}'`}</pre>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
